<!--
  Compose panel root: draggable (cdkDrag) and resizable (CSS resize: both).
  - Drag handle is the title span only; header buttons remain clickable.
  - width/height/margin-left bound for persistence and centering (margin-left = -width/2).
  - cdkDragFreeDragPosition restores saved position; positionChanged persists on drag end / before minimize.
  - bringToFront on mousedown and cdkDragStarted so this panel stacks on top when clicked or dragged.
-->
<div
  #dialogRoot
  class="compose-panel"
  [class.compose-panel--has-placeholders]="draftPlaceholders.length > 0"
  [class.compose-panel--has-attachments]="panelAttachments.length > 0"
  [class.compose-panel--has-placeholders-and-attachments]="draftPlaceholders.length > 0 && panelAttachments.length > 0"
  [style.z-index]="panel.zIndex"
  [style.width.px]="dialogWidth"
  [style.height.px]="dialogHeight"
  [style.margin-left.px]="dialogMarginLeft"
  [cdkDragFreeDragPosition]="{ x: panel.positionX ?? 0, y: panel.positionY ?? 0 }"
  (mousedown)="onPanelMouseDown()"
  cdkDrag
  cdkDragBoundary="body"
  (cdkDragStarted)="onDragStarted($event)"
  (cdkDragEnded)="onDragEnded($event)">
  <!-- Header: title is drag handle; minimize/close are normal buttons -->
  <div class="compose-panel__header">
    <span class="compose-panel__title" cdkDragHandle>
      <ui-svg-icon class="compose-panel__title-icon" [svgIcon]="getComposeTypeIcon(panel.type)" [size]="'medium'"></ui-svg-icon>
      <ng-container *ngIf="isDraft">{{ 'smart_inbox.compose.title_draft_prefix' | translate }}</ng-container>{{ (panel.type === 'compose' ? 'smart_inbox.compose.title_compose' : panel.type === 'reply' ? 'smart_inbox.compose.title_reply' : panel.type === 'reply-all' ? 'smart_inbox.compose.title_reply_all' : 'smart_inbox.compose.title_forward') | translate }}
    </span>
    <div class="compose-panel__header-actions">
      <button
        type="button"
        class="compose-panel__header-btn"
        mat-icon-button
        (click)="onMinimize(); $event.stopPropagation()"
        [attr.aria-label]="'smart_inbox.compose.aria_minimize' | translate">
        <mat-icon>remove</mat-icon>
      </button>
      <button
        type="button"
        class="compose-panel__header-btn"
        mat-icon-button
        (click)="onClose(); $event.stopPropagation()"
        [attr.aria-label]="'smart_inbox.compose.aria_close' | translate">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Body: From, To/Cc/Bcc, Subject, Editor -->
  <div class="compose-panel__body">
    <!-- From: editable select for new compose, read-only for reply/forward -->
    <div class="compose-panel__row">
      <label class="compose-panel__label">{{ 'smart_inbox.compose.label_from' | translate }}</label>
      <mat-form-field *ngIf="isNewCompose && senderAccounts.length" class="compose-panel__from-select" appearance="outline" subscriptSizing="dynamic">
        <mat-select [(ngModel)]="panel.from" [compareWith]="compareSender" [placeholder]="'smart_inbox.compose.placeholder_choose_sender' | translate" (selectionChange)="onSenderChange()">
          <mat-option *ngFor="let account of senderAccounts" [value]="account">
            {{ account.name }} &lt;{{ account.email }}&gt;
          </mat-option>
        </mat-select>
      </mat-form-field>
      <span *ngIf="!isNewCompose || !senderAccounts.length" class="compose-panel__from-value">{{ panel.from ? (panel.from.name + ' &lt;' + panel.from.email + '&gt;') : ('smart_inbox.compose.from_empty' | translate) }}</span>
    </div>

    <!-- To: chips + autocomplete; Cc/Bcc toggles -->
    <div class="compose-panel__row compose-panel__row-to">
      <label class="compose-panel__label">{{ 'smart_inbox.compose.label_to' | translate }}</label>
      <div class="compose-panel__chip-wrap">
        <mat-chip-list #toChipList>
          <mat-chip
            *ngFor="let email of toEmails"
            [removable]="true"
            (removed)="removeEmail('to', email)">
            {{ email }}
            <button matChipRemove [attr.aria-label]="'smart_inbox.compose.aria_remove' | translate">
              <mat-icon>cancel</mat-icon>
            </button>
          </mat-chip>
          <!-- autocomplete="one-time-code" disables browser autocomplete -->
          <input
            #toEmailInput
            type="text"
            autocomplete="one-time-code"
            name="compose-to"
            [placeholder]="'smart_inbox.compose.placeholder_to' | translate"
            [formControl]="toControl"
            [matChipInputFor]="toChipList"
            [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
            [matChipInputAddOnBlur]="true"
            [matAutocomplete]="toAutocomplete"
            (matChipInputTokenEnd)="addEmail('to', $event)" />
        </mat-chip-list>
        <mat-autocomplete #toAutocomplete="matAutocomplete" (optionSelected)="onAutocompleteSelected('to', $event.option.value)">
          <mat-option *ngFor="let email of filteredToSuggestions$ | async" [value]="email">
            {{ email }}
          </mat-option>
        </mat-autocomplete>
        <span class="compose-panel__links">
          <button type="button" class="compose-panel__link" (click)="toggleCc()">{{ 'smart_inbox.compose.link_cc' | translate }}</button>
          <button type="button" class="compose-panel__link" (click)="toggleBcc()">{{ 'smart_inbox.compose.link_bcc' | translate }}</button>
        </span>
      </div>
    </div>

    <ng-container *ngIf="showCc">
      <div class="compose-panel__row">
        <label class="compose-panel__label">{{ 'smart_inbox.compose.label_cc' | translate }}</label>
        <div class="compose-panel__chip-wrap">
          <mat-chip-list #ccChipList>
            <mat-chip
              *ngFor="let email of ccEmails"
              [removable]="true"
              (removed)="removeEmail('cc', email)">
              {{ email }}
              <button matChipRemove [attr.aria-label]="'smart_inbox.compose.aria_remove' | translate">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip>
            <!-- autocomplete="one-time-code" is used to prevent autocomplete from being triggered -->
            <input
              #ccEmailInput
              type="text"
              autocomplete="one-time-code"
              name="compose-cc"
              [placeholder]="'smart_inbox.compose.placeholder_cc' | translate"
              [formControl]="ccControl"
              [matChipInputFor]="ccChipList"
              [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
              [matChipInputAddOnBlur]="true"
              [matAutocomplete]="ccAutocomplete"
              (matChipInputTokenEnd)="addEmail('cc', $event)" />
          </mat-chip-list>
          <mat-autocomplete #ccAutocomplete="matAutocomplete" (optionSelected)="onAutocompleteSelected('cc', $event.option.value)">
            <mat-option *ngFor="let email of filteredCcSuggestions$ | async" [value]="email">
              {{ email }}
            </mat-option>
          </mat-autocomplete>
        </div>
      </div>
    </ng-container>
    <ng-container *ngIf="showBcc">
      <div class="compose-panel__row">
        <label class="compose-panel__label">{{ 'smart_inbox.compose.label_bcc' | translate }}</label>
        <div class="compose-panel__chip-wrap">
          <mat-chip-list #bccChipList>
            <mat-chip
              *ngFor="let email of bccEmails"
              [removable]="true"
              (removed)="removeEmail('bcc', email)">
              {{ email }}
              <button matChipRemove [attr.aria-label]="'smart_inbox.compose.aria_remove' | translate">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip>
            <!-- autocomplete="one-time-code" is used to prevent autocomplete from being triggered -->
            <input
              #bccEmailInput
              type="text"
              autocomplete="one-time-code"
              name="compose-bcc"
              [placeholder]="'smart_inbox.compose.placeholder_bcc' | translate"
              [formControl]="bccControl"
              [matChipInputFor]="bccChipList"
              [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
              [matChipInputAddOnBlur]="true"
              [matAutocomplete]="bccAutocomplete"
              (matChipInputTokenEnd)="addEmail('bcc', $event)" />
          </mat-chip-list>
          <mat-autocomplete #bccAutocomplete="matAutocomplete" (optionSelected)="onAutocompleteSelected('bcc', $event.option.value)">
            <mat-option *ngFor="let email of filteredBccSuggestions$ | async" [value]="email">
              {{ email }}
            </mat-option>
          </mat-autocomplete>
        </div>
      </div>
    </ng-container>

    <!-- Subject line -->
    <div class="compose-panel__row">
      <input
        type="text"
        class="compose-panel__input compose-panel__input-subject"
        [(ngModel)]="panel.subject"
        (input)="onSubjectInput()"
        [placeholder]="'smart_inbox.compose.placeholder_subject' | translate" />
    </div>

    <!-- AI Draft: idle button / generating banner / reasoning banner -->
    <ng-container *ngIf="isReplyType">
      <!-- Idle: show generate button -->
      <div class="compose-panel__ai-draft-row" *ngIf="!generatingAiDraft && !panel.aiDraft">
        <button
          type="button"
          class="compose-panel__ai-draft-btn"
          (click)="onAiDraft()">
          <ui-svg-icon svgIcon="ai-volta-icon" size="medium"></ui-svg-icon>
          <span class="compose-panel__ai-draft-label">{{ 'smart_inbox.compose.button_ai_draft' | translate }}</span>
        </button>
      </div>

      <!-- Generating: step-by-step loader -->
      <app-ai-generation-loader *ngIf="generatingAiDraft"></app-ai-generation-loader>

      <!-- Done: reasoning banner (collapsed by default) -->
      <div class="compose-panel__ai-reasoning-banner"
        [class.compose-panel__ai-reasoning-banner--generation-failed]="aiDraftGenerationFailed"
        *ngIf="!generatingAiDraft && panel.aiDraft && !reasoningBannerDismissed">
        <div class="compose-panel__ai-reasoning-header">
          <!-- Success: expandable reasoning toggle -->
          <button type="button" class="compose-panel__ai-reasoning-toggle" (click)="toggleReasoning()">
            <ui-svg-icon svgIcon="ai-volta-icon" size="small"></ui-svg-icon>
            <span *ngIf="!aiDraftGenerationFailed">{{ (reasoningExpanded ? 'smart_inbox.compose.ai_collapse_reasoning' : 'smart_inbox.compose.ai_expand_reasoning') | translate }}</span>
            <span *ngIf="aiDraftGenerationFailed">{{ 'smart_inbox.compose.ai_draft_generation_failed' | translate }}</span>
            <mat-icon class="compose-panel__ai-reasoning-chevron" [class.compose-panel__ai-reasoning-chevron--expanded]="reasoningExpanded" *ngIf="panel.aiDraft.reasoning || panel.aiDraft.processingErrors">expand_more</mat-icon>
          </button>
          <div role="button" class="compose-panel__ai-reasoning-close" [attr.aria-label]="'smart_inbox.compose.aria_close' | translate" (click)="dismissReasoningBanner()">
            <ui-svg-icon svgIcon="close" size="small"></ui-svg-icon>
          </div>
        </div>
        <div class="compose-panel__ai-reasoning-body" *ngIf="reasoningExpanded && (panel.aiDraft.reasoning || panel.aiDraft.processingErrors)">
          <p class="compose-panel__ai-reasoning-text" *ngIf="panel.aiDraft.processingErrors && aiDraftGenerationFailed">{{ panel.aiDraft.processingErrors }}</p>
          <p class="compose-panel__ai-reasoning-text" *ngIf="panel.aiDraft.reasoning && !aiDraftGenerationFailed">{{ panel.aiDraft.reasoning }}</p>
          <p class="compose-panel__ai-reasoning-note" *ngIf="!aiDraftGenerationFailed">{{ 'smart_inbox.compose.ai_reasoning_not_sent' | translate }}</p>
        </div>
      </div>
    </ng-container>

    <!-- Body editor: #editorWrap observed for --compose-editor-editable-max-height (resize with dialog) -->
    <div #editorWrap class="compose-panel__editor-wrap">
      <div #attachmentBtnWrap class="compose-panel__attachment-toolbar-wrap">
        <ui-button type="transparent" icon="thread-attachment" (click)="triggerAttachmentInput()"></ui-button>
      </div>
      <div class="compose-panel__ck-editor-wrapper">
        <ckeditor
          [editor]="Editor"
          [data]="initialEditorData"
          [config]="editorConfig"
          (ready)="onEditorReady($event)"
          (change)="onBodyChange($event)">
        </ckeditor>

        <!-- Placeholder overlay: clickable chips drawn over each [TOKEN] in the editor.
             Layer-relative coords so chips track the draggable panel. -->
        <div #phLayer class="compose-ph-layer">
          <button
            *ngFor="let chip of placeholderChips; trackBy: trackByChip"
            type="button"
            class="compose-ph-chip"
            [hidden]="activePlaceholderId === chip.id"
            [style.top.px]="chip.top"
            [style.left.px]="chip.left"
            [style.width.px]="chip.width"
            [style.height.px]="chip.height"
            (click)="onPlaceholderChipClick(chip)">
            <span class="compose-ph-chip__label">[{{ chip.id }}]</span>
          </button>

          <!-- Inline input over the clicked chip -->
          <div
            *ngIf="activePlaceholderId"
            class="compose-ph-overlay"
            [style.top.px]="activeInputTop"
            [style.left.px]="activeInputLeft"
            [style.width.px]="activeInputWidth"
            [style.height.px]="activeInputHeight">
            <input
              #phInput
              class="compose-ph-input"
              type="text"
              [placeholder]="getPlaceholderHint(activePlaceholderId)"
              (keydown.enter)="submitActivePlaceholder(phInput.value); $event.preventDefault()"
              (keydown.escape)="cancelActivePlaceholder()"
              (blur)="submitActivePlaceholder(phInput.value)" />
          </div>
        </div>
      </div>
      <input
        #attachmentInput
        type="file"
        class="compose-panel__file-input"
        multiple
        (change)="onFilesSelected($event)"
        tabindex="-1"
        aria-hidden="true" />
    </div>

    <div #quoteToggleEl class="compose-panel__quote-toggle" [style.display]="canShowQuoteToggle && !quoteExpanded ? 'flex' : 'none'" (click)="expandQuote()" [matTooltip]="'smart_inbox.compose.tooltip_show_trimmed' | translate" matTooltipPosition="right" matTooltipClass="conversation-tooltip-info">
      <span class="compose-panel__quote-dots">
        <span class="quote-toggle-dots">
          <ui-svg-icon svgIcon="more_horiz" size="medium"></ui-svg-icon>
        </span>
      </span>
    </div>

  </div>

  <div class="compose-panel__attachments" *ngIf="panelAttachments.length > 0">
    <div class="compose-panel__attachment"
         *ngFor="let att of panelAttachments; trackBy: trackByAttachment"
         [class.compose-panel__attachment--uploading]="isAttachmentUploading(att)"
         [attr.aria-busy]="isAttachmentUploading(att) ? 'true' : null"
         [attr.aria-label]="isAttachmentUploading(att) ? ('smart_inbox.compose.attachment_uploading' | translate:{ name: att.name }) : null">
      <ui-svg-icon class="compose-panel__attachment-icon" svgIcon="thread-attachment" size="small"></ui-svg-icon>
      <mat-spinner *ngIf="isAttachmentUploading(att)" class="compose-panel__attachment-spinner" diameter="16"></mat-spinner>
      <span class="compose-panel__attachment-name" [title]="att.name">{{ att.name }}</span>
      <span class="compose-panel__attachment-size">{{ att.size | fileSize }}</span>
      <div role="button"
        class="compose-panel__attachment-remove"
        (click)="removeAttachment(att)"
        [attr.aria-label]="'smart_inbox.compose.aria_remove_attachment' | translate">
        <ui-svg-icon svgIcon="close" size="small"></ui-svg-icon>
      </div>
    </div>
  </div>

  <!-- Footer actions (sticky when body scrolls) -->
  <div class="compose-panel__actions">
    <mat-spinner *ngIf="isSavingDraft" diameter="20" class="compose-panel__saving-spinner"></mat-spinner>
    <ui-button *ngIf="isDraft && !isSavingDraft" type="transparent" icon="trash-cc" (click)="onDeleteDraft()"></ui-button>
    <ui-button class="compose-panel__send-btn" type="primary" [label]="'smart_inbox.compose.button_send' | translate" icon="send-cc" [isLoading]="isLoading" [disabled]="isLoading || hasUploadingAttachments" (click)="onSend()"></ui-button>
  </div>
</div>
